import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Car, Loader2, Shield, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { getDashboardRoute } from '../config/routes';
import EditableText from '../components/EditableText';
import EditableImage from '../components/EditableImage';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [requires2FA, setRequires2FA] = useState(false);
  const [tempToken, setTempToken] = useState('');
  const [twoFactorCode, setTwoFactorCode] = useState('');
  const { login, loginWithGoogle, setUserAndToken } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await login(email, password);
      
      // Check if 2FA is required
      if (response.requires_2fa) {
        setRequires2FA(true);
        setTempToken(response.temp_token);
        toast.info('Please enter your 2FA code');
      } else {
        toast.success('Welcome back!');
        navigate(getDashboardRoute(response));
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  const handle2FAVerify = async (e) => {
    e.preventDefault();
    if (twoFactorCode.length !== 6) {
      toast.error('Please enter a 6-digit code');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/2fa/verify`, {
        temp_token: tempToken,
        code: twoFactorCode
      });
      
      // Set user and token in context
      if (setUserAndToken) {
        setUserAndToken(response.data.user, response.data.token);
      }
      localStorage.setItem('token', response.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      
      toast.success('Welcome back!');
      navigate(getDashboardRoute(response.data.user));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid 2FA code');
    } finally {
      setLoading(false);
    }
  };

  // 2FA Verification Screen
  if (requires2FA) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-8" data-testid="2fa-page">
        <div className="w-full max-w-md">
          <Link to="/" className="flex items-center gap-2 mb-8">
            <EditableImage
              brandKey="logo_image_url"
              imgClassName="w-10 h-10 rounded-full object-contain"
              fallback={
                <div className="w-10 h-10 bg-[#FF3008] rounded-full flex items-center justify-center">
                  <EditableText
                    brandKey="logo_letter"
                    defaultValue="J"
                    className="text-white font-extrabold text-lg"
                  />
                </div>
              }
              testId="2fa-logo"
            />
            <EditableText
              brandKey="app_name"
              defaultValue="Jiffys"
              className="text-xl font-extrabold text-[#191919]"
              testId="2fa-app-name"
            />
          </Link>

          <div className="bg-white p-8 rounded-lg shadow-sm border">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-[#FFF0ED] rounded-full flex items-center justify-center mx-auto mb-4">
                <Shield className="w-8 h-8 text-[#FF3008]" />
              </div>
              <h1 className="text-2xl font-bold text-gray-900 mb-2">Two-Factor Authentication</h1>
              <p className="text-gray-600">Enter the 6-digit code from your authenticator app</p>
            </div>

            <form onSubmit={handle2FAVerify} className="space-y-6">
              <div>
                <Label htmlFor="2fa-code" className="sr-only">Verification Code</Label>
                <Input
                  id="2fa-code"
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={6}
                  value={twoFactorCode}
                  onChange={(e) => setTwoFactorCode(e.target.value.replace(/\D/g, ''))}
                  placeholder="Enter 6-digit code"
                  className="h-14 text-center text-2xl tracking-[0.5em] font-mono"
                  required
                  autoFocus
                  data-testid="2fa-code-input"
                />
              </div>

              <Button 
                type="submit" 
                className="w-full h-12 bg-[#FF3008] hover:bg-[#D42600] text-white font-semibold rounded-full"
                disabled={loading || twoFactorCode.length !== 6}
                data-testid="2fa-submit"
              >
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Verify'}
              </Button>
            </form>

            <button 
              onClick={() => { setRequires2FA(false); setTwoFactorCode(''); }}
              className="mt-4 w-full text-center text-sm text-gray-600 hover:text-gray-900"
            >
              Back to login
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex relative overflow-hidden" data-testid="login-page">
      {/* Animated background */}
      <div className="absolute inset-0" style={{ background: 'linear-gradient(135deg, #FF3008 0%, #D42600 50%, #191919 100%)' }}>
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-20 left-20 w-72 h-72 bg-white rounded-full mix-blend-multiply filter blur-3xl animate-pulse"></div>
          <div className="absolute bottom-20 right-20 w-72 h-72 bg-orange-300 rounded-full mix-blend-multiply filter blur-3xl animate-pulse" style={{animationDelay: '1s'}}></div>
        </div>
      </div>

      {/* Left Panel - Form */}
      <div className="flex-1 flex items-center justify-center p-8 relative z-10">
        <div className="w-full max-w-md animate-fade-in-up">
          <Link to="/" className="flex items-center gap-3 mb-8" data-testid="login-logo">
            <EditableImage
              brandKey="logo_image_url"
              imgClassName="w-12 h-12 rounded-full object-contain shadow-lg"
              fallback={
                <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center shadow-lg">
                  <EditableText
                    brandKey="logo_letter"
                    defaultValue="J"
                    className="text-[#FF3008] font-extrabold text-xl"
                  />
                </div>
              }
              testId="login-logo-image"
            />
            <EditableText
              brandKey="app_name"
              defaultValue="Jiffys"
              className="text-2xl font-extrabold text-white drop-shadow-lg"
              testId="login-app-name"
            />
          </Link>

          <div className="glass p-8 rounded-2xl shadow-2xl animate-fade-in-scale stagger-2">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Welcome back</h1>
            <p className="text-gray-600 mb-8">Sign in to your account to continue</p>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="animate-fade-in-up stagger-1" style={{opacity: 0}}>
                <Label htmlFor="email" className="text-gray-700 font-medium">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="mt-1 h-12 rounded-xl border-gray-200 bg-white/80 focus:ring-2 focus:ring-[#FF3008] focus:border-transparent transition-all"
                  required
                  data-testid="login-email"
                />
              </div>

              <div className="animate-fade-in-up stagger-2" style={{opacity: 0}}>
                <div className="flex justify-between items-center">
                  <Label htmlFor="password" className="text-gray-700 font-medium">Password</Label>
                  <Link to="/forgot-password" className="text-sm text-[#FF3008] hover:text-[#D42600] font-medium">
                    Forgot password?
                  </Link>
                </div>
                <div className="relative mt-1">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    className="h-12 rounded-xl border-gray-200 bg-white/80 focus:ring-2 focus:ring-[#FF3008] focus:border-transparent pr-12 transition-all"
                    required
                    data-testid="login-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                  data-testid="toggle-password"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

              <Button 
                type="submit" 
                className="w-full h-12 bg-[#FF3008] hover:bg-[#D42600] text-white font-bold rounded-full shadow-lg hover:shadow-xl transition-all btn-press animate-fade-in-up stagger-3"
                style={{opacity: 0}}
                disabled={loading}
                data-testid="login-submit"
              >
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Sign In'}
              </Button>
            </form>

            <div className="relative my-8">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-200"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-4 bg-white/70 text-gray-500 rounded-full">or continue with</span>
              </div>
            </div>

            <Button
              type="button"
              variant="outline"
              className="w-full h-12 border-gray-200 text-gray-700 font-medium rounded-xl hover:bg-white/80 transition-all btn-press"
              onClick={loginWithGoogle}
              data-testid="login-google"
            >
              <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Continue with Google
            </Button>

            <p className="mt-8 text-center text-gray-600">
              Don't have an account?{' '}
              <Link to="/register" className="text-[#FF3008] font-semibold hover:text-[#D42600]" data-testid="login-register-link">
                Sign up
              </Link>
            </p>
          </div>
        </div>
      </div>

      {/* Right Panel - Hidden on mobile, gradient with animated elements */}
      <div className="hidden lg:flex lg:w-1/2 relative items-center justify-center overflow-hidden" style={{ background: '#191919' }}>
        <div className="text-center text-white z-10 px-12">
          <div className="w-20 h-20 bg-[#FF3008] rounded-full flex items-center justify-center mx-auto mb-8">
            <span className="text-white font-extrabold text-3xl">J</span>
          </div>
          <h2 className="text-4xl font-bold mb-4 drop-shadow-lg">24/7 Roadside Support</h2>
          <p className="text-xl text-gray-400 mb-8">Help is just a tap away, day or night.</p>
          <div className="flex justify-center gap-4">
            <div className="glass px-4 py-2 rounded-full text-sm">
              <span className="font-semibold">5,000+</span> Providers
            </div>
            <div className="glass px-4 py-2 rounded-full text-sm">
              <span className="font-semibold">100K+</span> Rescues
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
